// Monday receipt protection and the agent's intake/ownership policy. No I/O.
export const INBOUND_LEAD_TYPES = Object.freeze([
  "Anfrage",
  "Angebot",
  "Rückruf",
  "Kontaktformular",
  "LandVest-Analyse",
]);
export const LEAD_ADMISSION_POLICY = Object.freeze({
  startsAt: "2026-10-26T00:01:00+01:00",
  agentEmail: "platform@landvest.eu",
  clarificationEmail: "melanie.wagner@landvest.eu",
  inboundLeadTypes: INBOUND_LEAD_TYPES,
  excludedLeadSources: Object.freeze(["Marktrecherche 2024/25", "Marktrecherche 2026"]),
});
export const INITIAL_CLARIFICATION_MARKER = "[LA-CLARIFICATION-INITIAL]";
export const INITIAL_ADMISSION_MARKER = "[LA-INITIAL-ADMISSION]";

const RECEIPT_ENTITIES = Object.freeze({
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  lbrack: "[",
  lsqb: "[",
  colon: ":",
  hyphen: "-",
  dash: "-",
  tab: "\t",
  newline: "\n",
  nbsp: " ",
});

/**
 * Ordinary CRM writers may carry customer data, but may never mint the root
 * receipts trusted by the ownership service. Match the provider's visible text,
 * including entities/tags and GraphQL string escapes, before anything is sent.
 * Initial-admission provenance is deliberately non-authorizing and stays allowed.
 */
function assertNoReceiptText(source) {
  let text = source;
  for (let pass = 0; pass < 32; pass++) {
    const decoded = text
      .replace(/\\+u([0-9a-f]{4})/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
      .replace(/\\+[nrtfv]/g, " ")
      .replace(/<[^>]*>/g, "")
      .replace(/&#(x[0-9a-f]+|[0-9]+);?/gi, (whole, code) => {
        const value =
          code[0].toLowerCase() === "x" ? parseInt(code.slice(1), 16) : parseInt(code, 10);
        return value <= 0x10ffff ? String.fromCodePoint(value) : whole;
      })
      .replace(/&([a-z]+);?/gi, (whole, name) => RECEIPT_ENTITIES[name.toLowerCase()] ?? whole);
    if (/\[LA-(?:IDENTITY|HANDOVER|CLARIFICATION):/i.test(decoded.replace(/\s/g, ""))) {
      throw new Error("Reserved ownership receipts may only be written by the ownership service.");
    }
    if (decoded === text) return;
    text = decoded;
  }
  // Excessive nested encoding is not sent for a provider to interpret differently.
  throw new Error("Reserved ownership receipts: excessive nested encoding.");
}

export function assertNoOwnershipReceipt(query, variables) {
  if (!/\bmutation\b/.test(query)) return;
  const pending = [query, variables];
  const seen = new Set();
  while (pending.length) {
    const value = pending.pop();
    if (typeof value === "string") assertNoReceiptText(value);
    else if (value && typeof value === "object" && !seen.has(value)) {
      seen.add(value);
      for (const [key, entry] of Object.entries(value)) pending.push(key, entry);
    }
  }
}

/** A date-only or normalized impossible calendar date cannot establish an exact cutoff. */
export function exactInstant(value) {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)
  )
    return null;
  const local = new Date(`${value.slice(0, 19)}Z`);
  if (!Number.isFinite(local.getTime()) || local.toISOString().slice(0, 19) !== value.slice(0, 19))
    return null;
  const at = Date.parse(value);
  return Number.isFinite(at) ? at : null;
}

export function cohortHold({ receivedAt, createdAt, now, leadType, source }) {
  const cutoff = exactInstant(LEAD_ADMISSION_POLICY.startsAt);
  if (now < cutoff) return "cohort_not_started";
  const received = exactInstant(receivedAt);
  const created = createdAt === undefined ? now : exactInstant(createdAt);
  if (received === null || created === null || !Number.isFinite(now)) return "timestamp_unverified";
  if (received < cutoff || created < cutoff || received > now || created > now)
    return "outside_cutoff";
  if (!INBOUND_LEAD_TYPES.includes(leadType)) return "inbound_type_unverified";
  if (LEAD_ADMISSION_POLICY.excludedLeadSources.includes(source)) return "research_source_excluded";
  return null;
}

export function classifyOwner(owners, agentId) {
  if (
    !Array.isArray(owners) ||
    owners.some((p) => !p || !/^\d+$/.test(String(p.id)) || !["person", "team"].includes(p.kind))
  )
    return { kind: "unverified" };
  if (owners.length === 0) return { kind: "unassigned" };
  if (owners.length !== 1 || owners[0].kind !== "person") return { kind: "conflicting" };
  const personId = String(owners[0].id);
  return { kind: personId === String(agentId) ? "agent_owned" : "human_owned", personId };
}

export function classifyPersistentOwner({ owners, createdAt, agentId, now }) {
  const owner = classifyOwner(owners, agentId);
  if (owner.kind !== "unassigned") return owner;
  const created = exactInstant(createdAt);
  if (created === null || created < exactInstant(LEAD_ADMISSION_POLICY.startsAt) || created > now)
    return { kind: "unverified" };
  return owner;
}
